import { CalendarEventModel } from "../../../data/entities";
import { getEventEnd, getEventStart } from "../hooks";

interface AgendaTileProps {
  event: CalendarEventModel;
  now: Date;
  detailLine: "provider" | "location";
  onClick: (event: CalendarEventModel) => void;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function AgendaTile({ event, now, detailLine, onClick }: Readonly<AgendaTileProps>) {
  const start = getEventStart(event);
  const end = getEventEnd(event);

  const isActive = start <= now && now < end;
  const progress = isActive
    ? Math.min(100, Math.max(0, ((now.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 100))
    : 0;

  const isToday = start.toDateString() === now.toDateString();
  const dayLabel = isToday
    ? ""
    : start.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

  const detail = detailLine === "provider" ? event.providerName : event.locationName;

  return (
    <button
      type="button"
      className={`agenda-tile position-relative d-flex w-100 flex-shrink-0 text-start p-0 overflow-hidden${isActive ? " agenda-tile-active" : ""}`}
      onClick={() => onClick(event)}
    >
      <div
        className="agenda-tile-accent flex-shrink-0"
        style={event.serviceTypeColor ? { backgroundColor: event.serviceTypeColor } : undefined}
      />
      <div className="agenda-tile-body flex-grow-1">
        <div className="agenda-tile-time d-flex align-items-center gap-2 fw-semibold">
          <span>{formatTime(start)} – {formatTime(end)}</span>
          {dayLabel && <span className="agenda-tile-day fw-medium">{dayLabel}</span>}
          {isActive && <span className="agenda-tile-now-badge rounded-pill text-uppercase fw-bold">Now</span>}
        </div>
        <div className="agenda-tile-customer text-truncate fw-semibold text-primary">
          {event.customerFirstName} {event.customerLastName}
        </div>
        <div className="agenda-tile-detail text-truncate">
          {event.serviceName}
          {detail ? ` · ${detail}` : ""}
        </div>
      </div>
      {isActive && (
        <div className="agenda-tile-progress position-absolute start-0 end-0 bottom-0">
          <div className="agenda-tile-progress-bar h-100" style={{ width: `${progress}%` }} />
        </div>
      )}
    </button>
  );
}

export default AgendaTile;
